import type { PlayerProgress } from '../../shared/types';

/**
 * v1.1 星算門 active-skill pilot — pure logic (no Phaser).
 *
 * 學習與遊戲分離鐵則:此檔只「讀」progress.studentKPs 數學科 box level 換算
 * 戰力倍率,絕不在戰鬥中觸發答題。倍率只影響傷害,不參與答題流程。
 *
 * INVARIANT: pure functions + a tiny cooldown timer class. Unit-testable with
 * no Phaser / DOM dependency.
 */

/** Number of skill slots (1-4 keys). Slot 1 is the celestial pilot burst-fire. */
export const SKILL_SLOT_COUNT = 4;

/** Burst-fire (slot 1) tuning. 5 顆連發,120ms 間隔,冷卻 3 秒。 */
export const BURST = {
  shots: 5,
  intervalMs: 120,
  cooldownMs: 3000,
} as const;

/** Ultimate「時空爆裂」tuning — freeze 全場 2 秒。reuse ChargingState 充能。 */
export const ULTIMATE = {
  freezeMs: 2000,
} as const;

/** 星算門紫 (faction primary) — burst-fire 子彈視覺色。 */
export const CELESTIAL_PURPLE = 0x9c4dff;

/**
 * mastery box level (1-5) → 技能傷害倍率。
 * Lv1=1.0x / Lv2=1.25x / Lv3=1.5x / Lv4=1.75x / Lv5=2.0x
 */
export const MASTERY_DAMAGE_MULT: Record<1 | 2 | 3 | 4 | 5, number> = {
  1: 1.0,
  2: 1.25,
  3: 1.5,
  4: 1.75,
  5: 2.0,
};

/**
 * Compute the average 數學科 (math → 星算門) Leitner box level across the
 * player's studentKPs, clamped to 1..5. Returns 1 (no bonus) when the player
 * has no math KPs yet — a fresh player still gets the base 1.0x multiplier.
 *
 * Average is rounded to the nearest integer box so the倍率表 lookup is exact.
 */
export function masteryBoxLevelForMath(progress: PlayerProgress | null | undefined): 1 | 2 | 3 | 4 | 5 {
  const kps = progress?.studentKPs ?? [];
  const mathKps = kps.filter((k) => k.subject === 'math');
  if (mathKps.length === 0) return 1;
  const sum = mathKps.reduce((acc, k) => acc + (k.box ?? 1), 0);
  const avg = sum / mathKps.length;
  const rounded = Math.round(avg);
  return Math.min(5, Math.max(1, rounded)) as 1 | 2 | 3 | 4 | 5;
}

/** Damage multiplier for the current run, derived from math mastery. */
export function masteryDamageMultiplier(progress: PlayerProgress | null | undefined): number {
  return MASTERY_DAMAGE_MULT[masteryBoxLevelForMath(progress)];
}

/**
 * Per-slot cooldown timer. Pure: caller feeds wall-clock `nowMs`.
 *  - `tryActivate(nowMs)` returns true + starts cooldown when ready, else false.
 *  - `remainingMs(nowMs)` / `fraction(nowMs)` drive the HUD.
 */
export class CooldownSlot {
  private _readyAtMs = 0;
  constructor(public readonly cooldownMs: number) {}

  isReady(nowMs: number): boolean {
    return nowMs >= this._readyAtMs;
  }

  /** Attempt to fire. Starts cooldown and returns true only when ready. */
  tryActivate(nowMs: number): boolean {
    if (nowMs < this._readyAtMs) return false;
    this._readyAtMs = nowMs + this.cooldownMs;
    return true;
  }

  /** Milliseconds left on cooldown (0 when ready). */
  remainingMs(nowMs: number): number {
    return Math.max(0, this._readyAtMs - nowMs);
  }

  /** Cooldown progress 0 (just fired) .. 1 (ready). */
  fraction(nowMs: number): number {
    if (this.cooldownMs <= 0) return 1;
    const rem = this.remainingMs(nowMs);
    return Math.min(1, Math.max(0, 1 - rem / this.cooldownMs));
  }

  /** Force-reset to ready (scene restart). */
  reset(): void {
    this._readyAtMs = 0;
  }
}

/** Skill id written to progress.unlockedSkills when the boss is first beaten. */
export const CELESTIAL_L2_SKILL_ID = 'celestial-active-l2';
